import { useMemo, useState } from 'react';
import { ArrowDown, ArrowUp, Pencil, Plus, Tag, Trash2 } from 'lucide-react';
import { PainelConfirmDialog } from '@/components/painel/PainelFeedbackDialog';
import { PainelPermissionsDialogShell } from '@/components/painel/permissions/painel-role-visuals';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { painelInputClass, painelTextareaClass } from '@/components/painel/cms/painel-form';
import { CmsCategoryIcon, CMS_DEFAULT_ICON, CMS_ICON_OPTIONS } from '@/components/painel/cms/cms-icons';
import {
  painelButtonPrimaryClass,
  painelInsetCardClass,
  painelModalCancelButtonClass,
  painelSurfaceClass,
} from '@/components/painel/painel-styles';
import { PainelRocketScroll } from '@/components/painel/PainelRocketScroll';
import { ChangelogProductTabs } from '@/components/painel/changelog/changelog-product';
import { MOCK_CATEGORIES } from '@/components/painel/changelog/changelog-mock-data';
import { ChangelogPageShell } from '@/components/painel/changelog/changelog-ui';
import type { ChangelogCategoryDef } from '@/components/painel/changelog/changelog-types';
import { usePainelSaveFeedback } from '@/components/painel/PainelSaveFeedback';
import type { ChangelogProductId } from '@/lib/changelog/changelog-sources';
import { cn } from '@/lib/utils';

const THEME_COLORS: { hex: string; label: string }[] = [
  { hex: '#38bdf8', label: 'Sky' },
  { hex: '#60a5fa', label: 'Azul' },
  { hex: '#818cf8', label: 'Índigo' },
  { hex: '#a78bfa', label: 'Violeta' },
  { hex: '#c084fc', label: 'Roxo' },
  { hex: '#e879f9', label: 'Fúcsia' },
  { hex: '#ec4899', label: 'Pink' },
  { hex: '#fb7185', label: 'Rosa' },
  { hex: '#f87171', label: 'Vermelho' },
  { hex: '#fb923c', label: 'Laranja' },
  { hex: '#f97316', label: 'Laranja vivo' },
  { hex: '#fbbf24', label: 'Âmbar' },
  { hex: '#eab308', label: 'Amarelo' },
  { hex: '#a3e635', label: 'Lima' },
  { hex: '#4ade80', label: 'Verde' },
  { hex: '#34d399', label: 'Esmeralda' },
  { hex: '#2dd4bf', label: 'Teal' },
  { hex: '#22d3ee', label: 'Ciano' },
  { hex: '#94a3b8', label: 'Ardósia' },
];

const cardActionBtnClass = cn(
  'h-8 w-8 rounded-lg border border-white/15 bg-[#030023]/85 text-blue-100 shadow-sm backdrop-blur-sm',
  'hover:border-sky-400/40 hover:bg-sky-500/15 hover:text-white'
);

type CategoryFormState = {
  label: string;
  description: string;
  icon: string;
  emoji: string;
  color: string;
};

const emptyForm = (): CategoryFormState => ({
  label: '',
  description: '',
  icon: CMS_DEFAULT_ICON,
  emoji: '✨',
  color: '#38bdf8',
});

function newCategoryId() {
  return `cat-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 7)}`;
}

export function PainelChangelogCategoriesPage({ product }: { product: ChangelogProductId }) {
  const { showSaveSuccess } = usePainelSaveFeedback();
  const [items, setItems] = useState<ChangelogCategoryDef[]>(() =>
    [...MOCK_CATEGORIES].sort((a, b) => a.order - b.order)
  );
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<CategoryFormState>(emptyForm);
  const [deleteId, setDeleteId] = useState<string | null>(null);

  const editingItem = useMemo(
    () => (editingId ? items.find((c) => c.id === editingId) : undefined),
    [editingId, items]
  );

  const openCreate = () => {
    setEditingId(null);
    setForm(emptyForm());
    setDialogOpen(true);
  };

  const openEdit = (c: ChangelogCategoryDef) => {
    setEditingId(c.id);
    setForm({
      label: c.label,
      description: c.description,
      icon: c.icon,
      emoji: c.emoji,
      color: c.color,
    });
    setDialogOpen(true);
  };

  const handleSave = () => {
    const label = form.label.trim();
    if (!label) return;

    const payload = {
      label,
      description: form.description.trim(),
      icon: form.icon || CMS_DEFAULT_ICON,
      emoji: form.emoji.trim() || '✨',
      color: form.color || '#38bdf8',
    };

    if (editingId) {
      setItems((prev) => prev.map((c) => (c.id === editingId ? { ...c, ...payload } : c)));
    } else {
      setItems((prev) => [
        ...prev,
        {
          id: newCategoryId(),
          ...payload,
          order: prev.length,
        },
      ]);
    }

    setDialogOpen(false);
    setEditingId(null);
    showSaveSuccess();
  };

  const confirmDelete = () => {
    if (!deleteId) return;
    setItems((prev) =>
      prev.filter((c) => c.id !== deleteId).map((c, idx) => ({ ...c, order: idx }))
    );
    setDeleteId(null);
    showSaveSuccess();
  };

  const swap = (i: number, j: number) => {
    if (j < 0 || j >= items.length) return;
    const copy = [...items];
    const tmp = copy[i];
    copy[i] = copy[j];
    copy[j] = tmp;
    setItems(copy.map((it, idx) => ({ ...it, order: idx })));
  };

  return (
    <ChangelogPageShell className="space-y-6 pb-10">
      <ChangelogProductTabs product={product} />

      <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="font-poppins text-2xl font-bold text-white md:text-3xl">Categorias</h1>
          <p className="mt-1 font-inter text-sm text-slate-400">
            Modelos de secção para o editor — ícone, cor e texto de prévia.
          </p>
        </div>
        <Button type="button" className={painelButtonPrimaryClass} onClick={openCreate}>
          <Plus className="mr-2 h-4 w-4" />
          Nova categoria
        </Button>
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        {items.map((c, i) => (
          <div
            key={c.id}
            className={cn(
              painelSurfaceClass,
              'group relative overflow-hidden p-5 transition-all duration-300',
              'hover:-translate-y-0.5 hover:border-sky-400/30 hover:shadow-[0_24px_50px_-24px_rgba(56,189,248,0.25)]'
            )}
          >
            <div
              className="pointer-events-none absolute inset-x-0 top-0 h-24 opacity-60"
              style={{
                background: `radial-gradient(ellipse 70% 80% at 20% 0%, ${c.color}33, transparent)`,
              }}
              aria-hidden
            />

            <div className="absolute right-3 top-3 z-10 flex gap-1">
              <Button
                size="icon"
                type="button"
                variant="outline"
                className={cardActionBtnClass}
                aria-label="Mover para cima"
                disabled={i === 0}
                onClick={() => swap(i, i - 1)}
              >
                <ArrowUp className="h-3.5 w-3.5" />
              </Button>
              <Button
                size="icon"
                type="button"
                variant="outline"
                className={cardActionBtnClass}
                aria-label="Mover para baixo"
                disabled={i === items.length - 1}
                onClick={() => swap(i, i + 1)}
              >
                <ArrowDown className="h-3.5 w-3.5" />
              </Button>
              <Button
                size="icon"
                type="button"
                variant="outline"
                className={cardActionBtnClass}
                aria-label="Editar categoria"
                onClick={() => openEdit(c)}
              >
                <Pencil className="h-3.5 w-3.5" />
              </Button>
              <Button
                size="icon"
                type="button"
                variant="outline"
                className={cn(
                  cardActionBtnClass,
                  'text-rose-300 hover:border-rose-400/40 hover:bg-rose-500/15 hover:text-rose-100'
                )}
                aria-label="Excluir categoria"
                onClick={() => setDeleteId(c.id)}
              >
                <Trash2 className="h-3.5 w-3.5" />
              </Button>
            </div>

            <div className="relative flex items-start gap-4 pr-36">
              <div
                className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl ring-1 ring-white/15"
                style={{
                  backgroundColor: `${c.color}22`,
                  boxShadow: `0 0 28px -8px ${c.color}66`,
                }}
              >
                <CmsCategoryIcon name={c.icon} className="h-7 w-7" style={{ color: c.color }} />
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <h2 className="font-poppins text-base font-bold text-white">{c.label}</h2>
                  <span
                    className="inline-flex items-center gap-1.5 rounded-md px-2 py-0.5 font-mono text-[10px] text-slate-300"
                    style={{ backgroundColor: `${c.color}18` }}
                  >
                    <span
                      className="h-2 w-2 rounded-full"
                      style={{ backgroundColor: c.color }}
                      aria-hidden
                    />
                    {c.color}
                  </span>
                </div>
                <p className="mt-1.5 font-inter text-sm leading-relaxed text-slate-400">
                  {c.description || 'Sem descrição.'}
                </p>
                <div
                  className={cn(painelInsetCardClass, 'mt-4 border-l-4 p-3 text-sm text-blue-100/90')}
                  style={{ borderLeftColor: c.color }}
                >
                  <span className="font-poppins text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-400">
                    Prévia do bloco
                  </span>
                  <p className="mt-2 flex items-center gap-2 font-inter text-sm">
                    <CmsCategoryIcon name={c.icon} className="h-4 w-4 shrink-0" style={{ color: c.color }} />
                    <span>
                      <span className="font-semibold text-white">{c.label}</span>
                      <span className="text-slate-400"> — conteúdo do changelog…</span>
                    </span>
                    {c.emoji ? (
                      <span className="ml-auto text-base opacity-80" title="Emoji do editor" aria-hidden>
                        {c.emoji}
                      </span>
                    ) : null}
                  </p>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent
          showCloseButton={false}
          className="max-h-[90vh] max-w-lg overflow-hidden border-0 bg-[#030023] p-0 shadow-none"
        >
          <DialogTitle className="sr-only">
            {editingId ? 'Editar categoria' : 'Nova categoria'}
          </DialogTitle>
          <PainelPermissionsDialogShell>
            <div className="border-b border-white/10 bg-blue-950/20 px-6 pb-5 pt-6 sm:px-8">
              <div className="flex items-start gap-4 pr-14">
                <span className="inline-flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-sky-500/25 to-sky-700/10 ring-1 ring-sky-400/35 shadow-[0_0_40px_-10px_rgba(56,189,248,0.35)]">
                  <Tag className="h-7 w-7 text-sky-200" aria-hidden />
                </span>
                <div className="min-w-0 flex-1">
                  <p className="font-inter text-[10px] font-bold uppercase tracking-[0.18em] text-sky-300/55">
                    Changelog
                  </p>
                  <h2 className="mt-1 font-poppins text-xl font-bold text-white sm:text-2xl">
                    {editingId ? 'Editar categoria' : 'Nova categoria'}
                  </h2>
                  <p className="mt-1.5 font-inter text-sm text-slate-400">
                    {editingId
                      ? `Atualize o modelo${editingItem ? ` “${editingItem.label}”` : ''} usado nos blocos do editor.`
                      : 'Defina um modelo de secção com ícone, cor e emoji para o editor.'}
                  </p>
                </div>
              </div>
            </div>

            <PainelRocketScroll
              className='max-h-[min(52vh,420px)] bg-[#030023]/30'
              viewportClassName='px-6 py-5 sm:px-8 scroll-smooth'
            >
              <div className="grid gap-4">
                <div className="grid gap-2">
                  <Label htmlFor="ch-cat-label" className="font-inter text-xs font-medium text-blue-100/90">
                    Nome
                  </Label>
                  <input
                    id="ch-cat-label"
                    value={form.label}
                    onChange={(e) => setForm((f) => ({ ...f, label: e.target.value }))}
                    className={painelInputClass}
                    placeholder="Ex.: Novidades"
                  />
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="ch-cat-desc" className="font-inter text-xs font-medium text-blue-100/90">
                    Descrição
                  </Label>
                  <textarea
                    id="ch-cat-desc"
                    value={form.description}
                    onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                    className={cn(painelTextareaClass, 'min-h-[80px] resize-y')}
                    placeholder="Resumo curto do tipo de secção"
                  />
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="ch-cat-emoji" className="font-inter text-xs font-medium text-blue-100/90">
                    Emoji do editor (opcional)
                  </Label>
                  <input
                    id="ch-cat-emoji"
                    value={form.emoji}
                    onChange={(e) => setForm((f) => ({ ...f, emoji: e.target.value }))}
                    className={painelInputClass}
                    placeholder="✨"
                    maxLength={4}
                  />
                </div>
                <div className="grid gap-2">
                  <Label className="font-inter text-xs font-medium text-blue-100/90">Cor do tema</Label>
                  <div className="grid grid-cols-5 gap-2 sm:grid-cols-6">
                    {THEME_COLORS.map((opt) => {
                      const selected = form.color.toLowerCase() === opt.hex.toLowerCase();
                      return (
                        <button
                          key={opt.hex}
                          type="button"
                          onClick={() => setForm((f) => ({ ...f, color: opt.hex }))}
                          className={cn(
                            'flex flex-col items-center gap-1.5 rounded-xl border px-1.5 py-2 transition-all',
                            selected
                              ? 'border-sky-400/50 bg-sky-500/15 ring-1 ring-sky-400/30'
                              : 'border-white/10 bg-white/[0.03] hover:border-white/20 hover:bg-white/[0.06]'
                          )}
                          aria-pressed={selected}
                          title={opt.label}
                        >
                          <span
                            className="h-6 w-6 rounded-full shadow-inner ring-2 ring-white/15"
                            style={{ backgroundColor: opt.hex }}
                            aria-hidden
                          />
                          <span className="truncate font-inter text-[9px] font-medium text-blue-100/75">
                            {opt.label}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </div>
                <div className="grid gap-2">
                  <Label className="font-inter text-xs font-medium text-blue-100/90">Ícone</Label>
                  <div className="grid grid-cols-6 gap-1.5 sm:grid-cols-8">
                    {CMS_ICON_OPTIONS.map((opt) => {
                      const selected = form.icon === opt.value;
                      return (
                        <button
                          key={opt.value}
                          type="button"
                          onClick={() => setForm((f) => ({ ...f, icon: opt.value }))}
                          className={cn(
                            'inline-flex h-10 w-full items-center justify-center rounded-xl border transition-all',
                            selected
                              ? 'border-sky-400/50 bg-sky-500/15 text-sky-100 ring-1 ring-sky-400/30'
                              : 'border-white/10 bg-white/[0.03] text-blue-100/75 hover:border-white/20 hover:bg-white/[0.06] hover:text-white'
                          )}
                          aria-pressed={selected}
                          aria-label={opt.label}
                          title={opt.label}
                        >
                          <CmsCategoryIcon name={opt.value} className="h-[18px] w-[18px]" />
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>
            </PainelRocketScroll>

            <div className="flex flex-col-reverse gap-2 border-t border-white/10 bg-blue-950/25 px-6 py-4 sm:flex-row sm:justify-end sm:gap-3 sm:px-8">
              <Button
                type="button"
                variant="ghost"
                className={painelModalCancelButtonClass}
                onClick={() => setDialogOpen(false)}
              >
                Cancelar
              </Button>
              <Button
                type="button"
                className={painelButtonPrimaryClass}
                onClick={handleSave}
                disabled={!form.label.trim()}
              >
                Salvar
              </Button>
            </div>
          </PainelPermissionsDialogShell>
        </DialogContent>
      </Dialog>

      <PainelConfirmDialog
        open={deleteId !== null}
        onOpenChange={(o) => !o && setDeleteId(null)}
        title="Excluir categoria?"
        description="O modelo deixa de aparecer na lista de categorias do changelog. Blocos já criados no editor não são alterados."
        confirmLabel="Excluir"
        onConfirm={confirmDelete}
      />
    </ChangelogPageShell>
  );
}
